class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', this.handleUpsellAdd.bind(this));

    try {
      this.observeMilestones();
    } catch (e) {
      console.error('cart-confetti: milestone observer failed', e);
    }

    try {
      this.initUpsellCarousel();
    } catch (e) {
      console.error('cart-upsell: carousel init failed', e);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  observeMilestones() {
  if (this._milestoneObserver) return;

  this._milestoneObserver = new MutationObserver(() => {
    try {
      this.checkMilestoneConfetti();
    } catch (e) {
      console.error('cart-confetti: check failed', e);
    }
  });

  // Observe the custom element itself — it's never replaced, only its
  // inner content is, so this observer stays valid across re-renders.
  this._milestoneObserver.observe(this, {
    attributes: true,
    attributeFilter: ['class'],
    childList: true,
    subtree: true,
  });

  this.checkMilestoneConfetti();
}

checkMilestoneConfetti() {
  const drawerInner = this.querySelector('.drawer__inner') || this;
  const text = drawerInner.textContent || '';

  const isComplete = /congratulations/i.test(text) && /unlocked/i.test(text);
  const cartToken = (document.cookie.match(/cart=([^;]+)/) || [])[1] || '';

  console.log('cart-confetti debug:', { isComplete, cartToken, last: this.dataset.lastConfettiCart });

  if (isComplete && this.dataset.lastConfettiCart !== cartToken) {
    this.dataset.lastConfettiCart = cartToken;
    if (window.cartConfetti) window.cartConfetti.launch();
  } else if (!isComplete) {
    delete this.dataset.lastConfettiCart;
  }
}

  handleUpsellAdd(event) {
    const button = event.target.closest('[data-upsell-add]');
    if (!button || button.disabled || button.classList.contains('loading')) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.classList.add('loading');
    button.disabled = true;

    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: this.getSectionsToRender().map((s) => s.id).join(','),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) {
          console.error('cart-upsell: add to cart failed', parsedState);
          return;
        }
        this.renderContents(parsedState);
      })
      .catch((e) => {
        console.error('cart-upsell: add to cart error', e);
      })
      .finally(() => {
        button.classList.remove('loading');
        button.disabled = false;
      });
  }

initUpsellCarousel() {
  const carousel = this.querySelector('[data-upsell-carousel]');
  if (!carousel) return;

  const track = carousel.querySelector('[data-upsell-track]');
  const viewport = carousel.querySelector('.cart-drawer__upsell-viewport');
  const slides = track ? Array.from(track.children) : [];
  const prevBtn = this.querySelector('[data-upsell-prev]');
  const nextBtn = this.querySelector('[data-upsell-next]');
  const dotsWrap = this.querySelector('[data-upsell-dots]');

  if (!track || slides.length === 0) return;

  let index = 0;

  const buildDots = () => {
    if (!dotsWrap) return;
    dotsWrap.innerHTML = '';
    slides.forEach((_, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'cart-drawer__upsell-dot';
      dot.setAttribute('aria-label', `Go to product ${i + 1}`);
      dot.addEventListener('click', () => goTo(i));
      dotsWrap.appendChild(dot);
    });
  };

  const render = () => {
    track.style.transform = `translateX(-${index * 100}%)`;
    if (prevBtn) prevBtn.disabled = index === 0;
    if (nextBtn) nextBtn.disabled = index === slides.length - 1;
    if (dotsWrap) {
      Array.from(dotsWrap.children).forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }
  };

  const goTo = (i) => {
    index = Math.max(0, Math.min(i, slides.length - 1));
    render();
  };

  if (prevBtn) prevBtn.addEventListener('click', () => goTo(index - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => goTo(index + 1));

  // --- touch/swipe support ---
  if (viewport && slides.length > 1) {
    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let isDragging = false;
    let axisLocked = null; // 'x' or 'y', decided on first meaningful move

    const viewportWidth = () => viewport.getBoundingClientRect().width || 1;

    const onTouchStart = (e) => {
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      currentX = startX;
      isDragging = true;
      axisLocked = null;
      track.style.transition = 'none';
    };

    const onTouchMove = (e) => {
      if (!isDragging) return;
      const touch = e.touches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;

      if (axisLocked === null && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
        axisLocked = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (axisLocked === 'y') return; // let vertical scroll happen normally

      // horizontal swipe: prevent the page from scrolling
      e.preventDefault();
      currentX = touch.clientX;

      const dragPercent = (dx / viewportWidth()) * 100;
      let offset = -(index * 100) + dragPercent;

      // resistance at the edges
      if (index === 0 && dx > 0) offset = -(index * 100) + dragPercent * 0.35;
      if (index === slides.length - 1 && dx < 0) offset = -(index * 100) + dragPercent * 0.35;

      track.style.transform = `translateX(${offset}%)`;
    };

    const onTouchEnd = () => {
      if (!isDragging) return;
      isDragging = false;
      track.style.transition = '';

      if (axisLocked === 'x') {
        const dx = currentX - startX;
        const threshold = viewportWidth() * 0.18;
        if (dx <= -threshold) {
          goTo(index + 1);
        } else if (dx >= threshold) {
          goTo(index - 1);
        } else {
          render();
        }
      }
      axisLocked = null;
    };

    viewport.addEventListener('touchstart', onTouchStart, { passive: true });
    viewport.addEventListener('touchmove', onTouchMove, { passive: false });
    viewport.addEventListener('touchend', onTouchEnd);
    viewport.addEventListener('touchcancel', onTouchEnd);
  }

  buildDots();
  render();
}

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      try {
        this.observeMilestones();
      } catch (e) {
        console.error('cart-confetti: re-observe failed', e);
      }
      try {
        this.initUpsellCarousel();
      } catch (e) {
        console.error('cart-upsell: carousel re-init failed', e);
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);